import { usePermission } from "@/features/auth/hooks/usePermission";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import type {
  Permission,
  PermissionReason,
} from "features/auth/models/permissions";
import type { Maybe } from "types/utilities";

type WalletPermissionType = "deposit" | "withdraw";

/**
 * AUD accounts carry their own permission key, so reading the bare `deposit` /
 * `withdraw` key misses the reason an AUD user is blocked.
 */
const useWalletPermissionKey = (type: WalletPermissionType): Permission => {
  const currency = useFiatCurrency();
  return currency === "AUD" ? `${type}_AUD` : type;
};

/**
 * `isLoading` distinguishes "auth hasn't resolved" from "permission is absent",
 * which `usePermission` reports identically as `undefined`.
 */
const useWalletPermission = (
  type: WalletPermissionType,
): { permission: Maybe<PermissionReason>; isLoading: boolean } => {
  const isLoading = useAuthStore((state) => state.status === "loading");
  const permission = usePermission(useWalletPermissionKey(type));

  return {
    permission,
    isLoading,
  } as const;
};

export { useWalletPermission, useWalletPermissionKey };
export type { WalletPermissionType };
